import { Link } from "@tanstack/react-router";
import { Package } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { StatusBadge } from "@/components/shared/status-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useItems } from "@/hooks/use-items";
import { formatDate } from "@/lib/format";

interface PageState {
  cursor: string | null;
  direction: "next" | "prev";
}

const LIMIT = 50;

export function ItemsTable() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageRequest, setPageRequest] = useState<PageState>({
    cursor: null,
    direction: "next",
  });

  const { data, isLoading, isError, error, refetch } = useItems(
    { search: debouncedSearch, status: statusFilter },
    pageRequest,
  );

  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const hasNext = data?.hasNext ?? false;
  const hasPrev = data?.hasPrev ?? false;

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
    setPageRequest({ cursor: null, direction: "next" });
  }

  function handleStatusChange(value: string) {
    setStatusFilter(value === "all" ? "" : value);
    setPage(1);
    setPageRequest({ cursor: null, direction: "next" });
  }

  function handleNext() {
    if (data?.nextCursor) {
      setPage((p) => p + 1);
      setPageRequest({ cursor: data.nextCursor, direction: "next" });
    }
  }

  function handlePrev() {
    if (data?.prevCursor) {
      setPage((p) => Math.max(1, p - 1));
      setPageRequest({ cursor: data.prevCursor, direction: "prev" });
    }
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <SearchInput
            value={search}
            onValueChange={handleSearchChange}
            placeholder="Search items..."
            className="flex-1 max-w-sm"
          />
        </div>
        <ErrorState message={error?.message ?? "Failed to load items"} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-4">
        <SearchInput
          value={search}
          onValueChange={handleSearchChange}
          placeholder="Search items..."
          className="flex-1 max-w-sm"
        />
        <Select value={statusFilter || "all"} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <DataTable
        headers={[
          { label: "Name" },
          { label: "Status" },
          { label: "Description", className: "hidden md:table-cell" },
          { label: "Created", className: "hidden md:table-cell" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Package}
            title="No items found"
            description="Try adjusting your search or filters."
          />
        }
      >
        {items.map((item) => (
          <tr
            key={item.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 font-medium">
              <Link
                to="/dashboard/items/$itemId"
                params={{ itemId: item.id }}
                className="hover:text-primary transition-colors"
              >
                {item.name}
              </Link>
            </td>
            <td className="px-4 py-3">
              <StatusBadge status={item.status} />
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground max-w-xs truncate">
              {item.description || "\u2014"}
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
              {formatDate(item.createdAt)}
            </td>
          </tr>
        ))}
      </DataTable>

      {/* Pagination footer */}
      <CursorPagination
        total={total}
        page={page}
        limit={LIMIT}
        hasNext={hasNext}
        hasPrev={hasPrev}
        onNext={handleNext}
        onPrev={handlePrev}
        loading={isLoading}
        itemLabel="items"
      />
    </div>
  );
}
